import { fetchAllPages } from '@/lib/fetch-all-pages'
import { getSupabase } from '@/lib/supabase'
import { COD_REDUCTION_LIST_SELECT } from './cod-reductions-repository'
import { applyCodReductionFilter, dayStartIso, nextDayStartIso } from './cod-reduction-filter'
import type {
  CodReductionListFilter,
  CodReductionListResult,
  CodReductionListRow,
  CodReductionStatus,
} from '@/types/db'

const TABLE = 'cod_reduction_requests'
// Trần CHỦ ĐÍCH của file Excel (đọc theo lô — không dính trần 1000 dòng của PostgREST).
export const COD_REDUCTION_EXPORT_MAX = 10_000

// Builder của supabase-js khó type cho chuỗi điều kiện động — như applyFilter của Duyệt chi.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyBuilder = any

export async function listCodReductions(filter: CodReductionListFilter = {}): Promise<CodReductionListResult> {
  const page = Math.max(1, filter.page ?? 1)
  const pageSize = Math.min(100, Math.max(5, filter.pageSize ?? 20))
  const offset = (page - 1) * pageSize
  const base: AnyBuilder = getSupabase().from(TABLE).select(COD_REDUCTION_LIST_SELECT, { count: 'exact' })
  const { data, error, count } = await applyCodReductionFilter(base, filter)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(offset, offset + pageSize - 1)
  if (error) throw error
  return { rows: (data ?? []) as CodReductionListRow[], total: count ?? 0 }
}

// Tổng tiền hạ + số đề nghị theo ĐÚNG bộ lọc (mọi trang) — dải tổng của danh sách.
export async function sumCodReductions(filter: CodReductionListFilter = {}): Promise<{ count: number; sum: number }> {
  const rows = await fetchAllPages<{ reduce_amount: number }>(
    (from, to) => {
      const base: AnyBuilder = getSupabase().from(TABLE).select('id, reduce_amount')
      return applyCodReductionFilter(base, { ...filter, page: undefined, pageSize: undefined })
        .order('id', { ascending: true })
        .range(from, to)
    },
    1000,
    COD_REDUCTION_EXPORT_MAX,
  )
  return { count: rows.length, sum: rows.reduce((s, r) => s + r.reduce_amount, 0) }
}

// Số đề nghị đang chờ CHÍNH MÌNH xử lý (badge tab + sidebar). Head count — không tải dòng.
export async function countMyTurnCodReductions(): Promise<number> {
  const { count, error } = await getSupabase()
    .from(TABLE)
    .select('id', { count: 'exact', head: true })
    .in('status', ['pending', 'approved'])
    .eq('cod_reduction_is_my_turn', true)
  if (error) throw error
  return count ?? 0
}

// Báo cáo: đề nghị ĐÃ HẠ trong kỳ (theo ngày hạ COD). Đọc theo lô, ORDER có khoá `id`.
export async function fetchCompletedCodReductions(range: {
  from?: string | null
  to?: string | null
}): Promise<CodReductionListRow[]> {
  return fetchAllPages<CodReductionListRow>((from, to) => {
    let q: AnyBuilder = getSupabase().from(TABLE).select(COD_REDUCTION_LIST_SELECT).eq('status', 'completed')
    if (range.from) q = q.gte('completed_at', dayStartIso(range.from))
    if (range.to) q = q.lt('completed_at', nextDayStartIso(range.to))
    return q.order('id', { ascending: true }).range(from, to)
  })
}

// Đã hạ COD mà đơn được hãng ĐỐI SOÁT trong kỳ (lọc theo ngày đối soát — computed field),
// bất kể hạ lúc nào: phiếu thu tự động lúc đối soát ghi theo COD CŨ (dư đúng số đã hạ) →
// kế toán soát theo kỳ đối soát để sửa tay (Hạ COD chỉ ghi nhận — chốt 26/10/2026).
export async function fetchNeedAdjustCodReductions(range: {
  from?: string | null
  to?: string | null
}): Promise<CodReductionListRow[]> {
  return fetchAllPages<CodReductionListRow>((from, to) => {
    let q: AnyBuilder = getSupabase()
      .from(TABLE)
      .select(COD_REDUCTION_LIST_SELECT)
      .eq('status', 'completed')
      .not('cod_reduction_order_reconciled_at', 'is', null)
    if (range.from) q = q.gte('cod_reduction_order_reconciled_at', dayStartIso(range.from))
    if (range.to) q = q.lt('cod_reduction_order_reconciled_at', nextDayStartIso(range.to))
    return q.order('id', { ascending: true }).range(from, to)
  })
}

// Trạng thái các đề nghị TẠO trong kỳ (thẻ "đang chờ / bị từ chối / huỷ" của báo cáo).
export async function fetchCodReductionStatusesCreated(range: {
  from?: string | null
  to?: string | null
}): Promise<{ status: CodReductionStatus }[]> {
  return fetchAllPages<{ status: CodReductionStatus }>((from, to) => {
    let q: AnyBuilder = getSupabase().from(TABLE).select('id, status')
    if (range.from) q = q.gte('created_at', dayStartIso(range.from))
    if (range.to) q = q.lt('created_at', nextDayStartIso(range.to))
    return q.order('id', { ascending: true }).range(from, to)
  })
}

// Xuất Excel theo ĐÚNG bộ lọc danh sách (mọi trang, trần COD_REDUCTION_EXPORT_MAX).
export async function fetchCodReductionsForExport(filter: CodReductionListFilter = {}): Promise<CodReductionListRow[]> {
  return fetchAllPages<CodReductionListRow>(
    (from, to) => {
      const base: AnyBuilder = getSupabase().from(TABLE).select(COD_REDUCTION_LIST_SELECT)
      return applyCodReductionFilter(base, { ...filter, page: undefined, pageSize: undefined })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to)
    },
    1000,
    COD_REDUCTION_EXPORT_MAX,
  )
}
